import { useCallback, useEffect, useRef, useState } from "react";
import { describeCoreError } from "../../core-api/errors";
import {
  loadAuthenticatedWorkspaces,
  type AuthenticatedWorkspace,
} from "../../core-api/auth";
import {
  archiveTeam,
  createTeam,
  loadTeam,
  loadTeamMembers,
  loadTeams,
  loadTeamUsers,
  updateTeam,
  type TeamCapabilities,
  type TeamDirectoryUser,
  type TeamSummary,
} from "./api";
import "./teams.css";

export function TeamManagement() {
  const [workspaces, setWorkspaces] = useState<AuthenticatedWorkspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    loadAuthenticatedWorkspaces(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setWorkspaces(result.workspaces);
        setWorkspaceId(
          result.workspaces.some(
            (workspace) => workspace.id === result.selectedWorkspaceId,
          )
            ? result.selectedWorkspaceId!
            : (result.workspaces[0]?.id ?? ""),
        );
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(describeCoreError(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  const selected = workspaces.find((item) => item.id === workspaceId);
  return (
    <section className="team-management" aria-label="团队管理">
      <header className="team-page-header">
        <h1>团队管理</h1>
        <p>按工作空间管理团队与成员。</p>
      </header>
      {error && (
        <p role="alert" className="team-message error">
          {error}
        </p>
      )}
      <div className="team-layout">
        <aside className="team-sidebar" aria-label="工作空间选择">
          <h2>工作空间</h2>
          {loading && <p role="status">加载中…</p>}
          {!loading && !workspaces.length && <p>暂无可访问的工作空间</p>}
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              type="button"
              aria-pressed={workspace.id === workspaceId}
              onClick={() => setWorkspaceId(workspace.id)}
            >
              <strong>{workspace.name}</strong>
              <small>{workspace.code}</small>
            </button>
          ))}
        </aside>
        {selected ? (
          <WorkspaceTeams key={selected.id} workspace={selected} />
        ) : (
          <main className="team-detail" />
        )}
      </div>
    </section>
  );
}

function WorkspaceTeams({ workspace }: { workspace: AuthenticatedWorkspace }) {
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [capabilities, setCapabilities] = useState<TeamCapabilities>();
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editor, setEditor] = useState<{
    mode: "create" | "edit" | "delete";
    team?: TeamSummary;
  }>();
  const [users, setUsers] = useState<TeamDirectoryUser[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState("");
  const dialog = useRef<HTMLDialogElement>(null);
  const generation = useRef(0);
  const locked = useRef(false);
  const reload = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError("");
      try {
        const result = await loadTeams(workspace.id, signal);
        if (signal?.aborted) return;
        setTeams(result.items);
        setCapabilities(result.capabilities);
      } catch (cause) {
        if (!signal?.aborted) setError(describeCoreError(cause));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [workspace.id],
  );
  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal);
    return () => {
      controller.abort();
      generation.current++;
    };
  }, [reload]);
  useEffect(() => {
    if (editor) dialog.current?.showModal();
    else dialog.current?.close();
  }, [Boolean(editor)]);
  function closeEditor() {
    if (locked.current) return;
    generation.current++;
    setEditor(undefined);
  }
  async function openEditor(
    mode: "create" | "edit" | "delete",
    team?: TeamSummary,
  ) {
    const request = ++generation.current;
    setEditor({ mode, team });
    setName(team?.name ?? "");
    setDescription(team?.description ?? "");
    setReason("");
    setEditorError("");
    setMembers([]);
    setUsers([]);
    setEditorLoading(false);
    if (mode === "delete") return;
    setEditorLoading(true);
    try {
      const [fresh, directory, membership] = await Promise.all([
        team ? loadTeam(team.id, workspace.id) : Promise.resolve(undefined),
        capabilities?.readMembers
          ? loadTeamUsers(workspace.id)
          : Promise.resolve({ items: [] }),
        team && capabilities?.readMembers
          ? loadTeamMembers(team.id, workspace.id)
          : Promise.resolve({ items: [] }),
      ]);
      if (request !== generation.current) return;
      if (fresh) {
        setEditor({ mode, team: fresh });
        setName(fresh.name);
        setDescription(fresh.description);
      }
      setUsers(directory.items);
      const now = Date.now();
      setMembers(
        membership.items
          .filter(
            (item) =>
              item.status === "ACTIVE" &&
              (!item.validFrom || Date.parse(item.validFrom) <= now) &&
              (!item.validTo || Date.parse(item.validTo) > now) &&
              directory.items.some((user) => user.id === item.userId),
          )
          .map((item) => item.userId),
      );
    } catch (cause) {
      if (request === generation.current)
        setEditorError(describeCoreError(cause));
    } finally {
      if (request === generation.current) setEditorLoading(false);
    }
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!editor || locked.current || editorLoading) return;
    locked.current = true;
    setBusy(true);
    setEditorError("");
    setNotice("");
    const request = generation.current;
    try {
      if (editor.mode === "delete" && editor.team) {
        await archiveTeam(
          editor.team.id,
          editor.team.version,
          reason.trim(),
          workspace.id,
        );
      } else {
        const input = {
          name: name.trim(),
          description: description.trim(),
          reason: editor.mode === "create" ? "新建团队" : "更新团队",
          ...(capabilities?.manageMembers && capabilities.readMembers
            ? { memberUserIds: members }
            : {}),
        };
        if (editor.team)
          await updateTeam(
            editor.team.id,
            { ...input, expectedVersion: editor.team.version },
            workspace.id,
          );
        else await createTeam(input, workspace.id);
      }
      if (request !== generation.current) return;
      setEditor(undefined);
      setNotice(editor.mode === "delete" ? "团队已删除。" : "团队已保存。");
      await reload();
    } catch (cause) {
      if (request === generation.current)
        setEditorError(describeCoreError(cause));
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }
  async function restore(team: TeamSummary) {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      await updateTeam(
        team.id,
        { expectedVersion: team.version, status: "ACTIVE", reason: "恢复团队" },
        workspace.id,
      );
      await reload();
    } catch (cause) {
      setError(describeCoreError(cause));
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }
  const filtered = teams.filter(
    (team) =>
      (showArchived || team.status === "ACTIVE") &&
      team.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  return (
    <main className="team-detail" aria-label={workspace.name + "的团队"}>
      <div className="team-toolbar">
        <label>
          按名称筛选
          <input
            value={search}
            placeholder="输入团队名称"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="team-check">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
          显示已删除团队
        </label>
        <button
          type="button"
          disabled={loading || busy}
          onClick={() => void reload()}
        >
          刷新
        </button>
        {capabilities?.manageTeams && (
          <button
            type="button"
            className="primary"
            disabled={busy || loading}
            onClick={() => void openEditor("create")}
          >
            新建团队
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="team-message error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="team-message success">
          {notice}
        </p>
      )}
      {loading ? (
        <p role="status">正在加载团队…</p>
      ) : (
        <div className="team-table-wrap">
          <table>
            <thead>
              <tr>
                <th>所属工作空间</th>
                <th>名称</th>
                <th>人员数量</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((team) => (
                <tr key={team.id}>
                  <td>{workspace.name}</td>
                  <td>
                    {team.name}
                    {team.status === "ARCHIVED" && <small>（已删除）</small>}
                  </td>
                  <td>{team.activeMemberCount}</td>
                  <td>
                    {new Date(team.createdAt).toLocaleString("zh-CN", {
                      hour12: false,
                    })}
                  </td>
                  <td>
                    <div className="team-row-actions">
                      {capabilities?.manageTeams &&
                        (team.status === "ACTIVE" ? (
                          <>
                            <button
                              disabled={busy}
                              onClick={() => void openEditor("edit", team)}
                            >
                              更新团队
                            </button>
                            <button
                              className="danger"
                              disabled={busy || team.caseCount > 0}
                              title={
                                team.caseCount > 0
                                  ? "团队仍绑定案件，全部解绑后才能删除"
                                  : "删除团队"
                              }
                              onClick={() => void openEditor("delete", team)}
                            >
                              删除团队
                            </button>
                          </>
                        ) : (
                          <button
                            disabled={busy}
                            onClick={() => void restore(team)}
                          >
                            恢复团队
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && <p className="team-empty">没有匹配的团队</p>}
        </div>
      )}
      <dialog
        ref={dialog}
        className="team-dialog"
        aria-labelledby={"team-dialog-" + workspace.id}
        onCancel={(event) => {
          event.preventDefault();
          closeEditor();
        }}
        onClose={() => {
          if (!locked.current) setEditor(undefined);
        }}
      >
        <form className="team-form" onSubmit={(event) => void save(event)}>
          <div className="team-modal-heading">
            <h2 id={"team-dialog-" + workspace.id}>
              {editor?.mode === "delete"
                ? "删除团队"
                : editor?.mode === "edit"
                  ? "更新团队"
                  : "新建团队"}
            </h2>
            <button
              type="button"
              aria-label="关闭弹窗"
              disabled={busy}
              onClick={closeEditor}
            >
              ×
            </button>
          </div>
          <p className="team-muted">所属工作空间：{workspace.name}</p>
          {editorError && (
            <p role="alert" className="team-message error">
              {editorError}
            </p>
          )}
          {editor?.mode === "delete" ? (
            <>
              <p>确认删除“{editor.team?.name}”？团队将软删除，保留历史记录。</p>
              <label>
                删除原因
                <textarea
                  required
                  maxLength={500}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  disabled={busy}
                />
              </label>
            </>
          ) : (
            <>
              <label>
                团队名称
                <input
                  required
                  maxLength={160}
                  value={name}
                  disabled={busy || editorLoading}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label>
                团队说明
                <textarea
                  maxLength={2000}
                  value={description}
                  disabled={busy || editorLoading}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </label>
              <fieldset
                disabled={busy || editorLoading || !capabilities?.manageMembers}
              >
                <legend>团队成员</legend>
                <p className="team-muted">
                  仅可选择已绑定该工作空间的有效用户。
                </p>
                {editorLoading ? (
                  <p role="status">正在加载成员…</p>
                ) : !capabilities?.readMembers ? (
                  <p>没有查看团队成员的权限</p>
                ) : (
                  <div className="team-member-picker">
                    {users.map((user) => (
                      <label className="team-check" key={user.id}>
                        <input
                          type="checkbox"
                          checked={members.includes(user.id)}
                          onChange={(event) =>
                            setMembers((current) =>
                              event.target.checked
                                ? [...current, user.id]
                                : current.filter((id) => id !== user.id),
                            )
                          }
                        />
                        <span>
                          {user.displayName}
                          <small>{user.username ?? user.primaryEmail}</small>
                        </span>
                      </label>
                    ))}
                    {!users.length && <p>该工作空间暂无有效用户</p>}
                  </div>
                )}
              </fieldset>
            </>
          )}
          <div className="team-modal-actions">
            <button type="button" disabled={busy} onClick={closeEditor}>
              取消
            </button>
            <button
              className={editor?.mode === "delete" ? "danger" : "primary"}
              disabled={
                busy ||
                editorLoading ||
                (editor?.mode === "delete" ? !reason.trim() : !name.trim()) ||
                Boolean(editorError)
              }
            >
              {busy
                ? "保存中…"
                : editor?.mode === "delete"
                  ? "确认删除"
                  : "保存团队"}
            </button>
            {editorError && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  editor && void openEditor(editor.mode, editor.team)
                }
              >
                重新载入
              </button>
            )}
          </div>
        </form>
      </dialog>
    </main>
  );
}
